import type { ListingSellerContact } from "./listingSellerStore";

/**
 * WHICH CONTACT OF MINE IS THIS LISTING'S OWNER — one answer for every place that shows a
 * listing's seller side: the board's row and grid card, the save button beside them, the
 * property card, and the task or meeting the board creates on the listing.
 *
 * ## Why one answer (Trello #254, follow-up of 2026-11-04)
 *
 * The board's save button learned to find an owner saved under the real number (the link the
 * save writes, `useListingSellerContacts`), but only the BUTTON asked. The row beside it kept
 * printing the Yad2 mask as the owner's phone, the property card under it printed the mask, and
 * a meeting made from the row invited the mask — so the realtor saw a green chip next to a
 * number she had already replaced, and her support rep reported it as "the bug is fixed but the
 * number is still virtual". Every surface now asks this, and they cannot disagree.
 *
 * ## Precedence — the same as the button's
 *
 *  1. The number the AD carries is itself a contact of mine (`via: 'phone'`): the row's own
 *     number is the right one, and nothing on the row changes.
 *  2. A contact of mine LINKED to this listing as its seller / landlord (`via: 'listing'`): the
 *     owner she saved under the number she typed — usually the real one behind a Yad2 mask. This
 *     is the case the surfaces must show instead of the ad.
 *
 * ## Three states
 *
 *   undefined   still resolving (either question unanswered and nothing found yet)
 *   null        answered: nobody of mine stands for this listing's owner
 *   {…}         this contact
 *
 * A listing answer that FAILED counts as answered with nobody (the behaviour before the link
 * existed), so a failure never hides the save button for good.
 */
export type SavedListingSeller = {
  /** The contact's document id — its phone, in E.164. */
  contactId: string;
  /** The saved name, when the contact has one. */
  name?: string;
  /** The contact's own number: the one to show, dial, copy and invite. */
  phone: string;
  /** How many MORE contacts are linked to the listing as its seller side (a couple, a duplicate). */
  others: number;
  via: "phone" | "listing";
};

/** What `useContactByPhone` answered, in the shape this needs. */
export type PhoneContactAnswer = {
  /** `undefined` = still resolving. */
  exists: boolean | undefined;
  contact?: {
    id?: string;
    callerPhoneNumber?: string;
    callerFirstName?: string;
    callerLastName?: string;
  } | null;
};

const clean = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** "first last", or undefined when neither is set. */
export function fullNameOf(firstName?: string | null, lastName?: string | null): string | undefined {
  return clean([clean(firstName), clean(lastName)].filter(Boolean).join(" "));
}

export function pickSavedListingSeller({
  phoneAnswer,
  linked,
  linkFailed,
  asksListing
}: {
  phoneAnswer: PhoneContactAnswer;
  /** `useListingSellerContacts().sellers` — undefined until answered. */
  linked: readonly ListingSellerContact[] | undefined;
  /** `useListingSellerContacts().failed`. */
  linkFailed: boolean;
  /** Whether the listing question is asked at all (a listing id, and enabled). */
  asksListing: boolean;
}): SavedListingSeller | null | undefined {
  const linkedSellers = linked ?? [];

  const phoneContact = phoneAnswer.exists ? phoneAnswer.contact : undefined;
  const phoneContactId = clean(phoneContact?.id) ?? clean(phoneContact?.callerPhoneNumber);
  if (phoneContact && phoneContactId) {
    return {
      contactId: phoneContactId,
      name: fullNameOf(phoneContact.callerFirstName, phoneContact.callerLastName),
      phone: clean(phoneContact.callerPhoneNumber) ?? phoneContactId,
      others: linkedSellers.filter((seller) => seller.contactId !== phoneContactId).length,
      via: "phone"
    };
  }

  const [first, ...rest] = linkedSellers;
  if (first) {
    return {
      contactId: first.contactId,
      name: clean(first.name),
      phone: first.contactId,
      others: rest.length,
      via: "listing"
    };
  }

  const phoneAnswered = phoneAnswer.exists !== undefined;
  const listingAnswered = !asksListing || linked !== undefined || linkFailed;
  return phoneAnswered && listingAnswered ? null : undefined;
}

/**
 * The name a surface prints for the saved owner: the saved name ("+N" when more contacts are
 * linked), else nothing — the caller then falls back to the number, never to the AD's name.
 */
export function savedSellerLabel(seller: Pick<SavedListingSeller, "name" | "others">): string | undefined {
  if (!seller.name) return seller.others > 0 ? `+${seller.others}` : undefined;
  return seller.others > 0 ? `${seller.name} +${seller.others}` : seller.name;
}

/**
 * Should a surface show the SAVED owner in place of what the ad says? Only when the saved owner
 * came through the listing link: then the ad's number is not theirs (usually a Yad2 mask). When
 * the ad's own number is the contact (`via: 'phone'`), the ad already shows the right person.
 */
export function showsSavedOwnerInsteadOfAd(
  seller: SavedListingSeller | null | undefined
): seller is SavedListingSeller {
  return Boolean(seller && seller.via === "listing");
}
